import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  AppState, 
  AppAction, 
  Role, 
  UserProfile,
  Household,
  HouseholdMember
} from '@/types'; 
import { initializeHousehold, joinHouseholdWithCode } from '../features/household-engine/components/householdService';
import { User } from 'firebase/auth'; // Import User type for initializeHousehold
import { Button } from '@/components/ui/Button';
import { db } from '@/firebase';
import { 
  getDoc,
  setDoc,
  doc, 
  updateDoc,
  deleteDoc,
  deleteField,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import {
  Shield, Settings, Users, Home, Trash2, Mail,
  CheckCircle2, RefreshCw, Copy, XCircle, Plus, Clock,
  DoorOpen, ChevronDown, Loader2, Zap, LayoutGrid,
  ArrowRight, UserPlus, Building2, AlertCircle
} from 'lucide-react';

export const HouseholdSettings: React.FC<{ state: AppState; dispatch: React.Dispatch<AppAction> }> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  
  console.log("HouseholdSettings: state.currentUser =", state.currentUser);

  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'MEMBERS' | 'SWITCHER'>(state.currentHouseholdId ? 'OVERVIEW' : 'SWITCHER');
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isCreatingHousehold, setIsCreatingHousehold] = useState(false); 
  const [isJoining, setIsJoining] = useState(false);
  const [maxHouseholdsPerUser, setMaxHouseholdsPerUser] = useState<number>(3);
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [newHouseholdNameError, setNewHouseholdNameError] = useState<string | null>(null); // 🌟 NEW State for input error
  const [joinCodeError, setJoinCodeError] = useState<string | null>(null);

  const currentUserId = state.currentUser?.id || '';

  const currentUser = useMemo(() => {
    // Create a mock Firebase User object from UserProfile for householdService functions
    if (state.currentUser && currentUserId && state.currentUser.email) {
      return {
        uid: currentUserId,
        email: state.currentUser.email,
        displayName: state.currentUser.name || state.currentUser.email,
        photoURL: state.currentUser.avatar || '',
        // Add other properties if householdService explicitly uses them (though uid, email, displayName are most common)
      } as User;
    }
    return null;
  }, [state.currentUser, currentUserId]);

  console.log("HouseholdSettings: memoized currentUser =", currentUser);

  // 🛰️ Household Lookup - Now correctly handles `undefined` if no currentHouseholdId
  const household: Household | undefined = useMemo(() => {
    if (!state.currentHouseholdId) {
      return undefined;
    }
    return (state.households || []).find((h: Household) => h.id === state.currentHouseholdId);
  }, [state.households, state.currentHouseholdId]);

  // DERIVED MEMBERS FOR DISPLAY (This is the core fix)
  const householdMembersForDisplay = useMemo(() => {
    if (!household || !state.userProfiles) return [];

    return household.memberIds.map(memberId => {
      const userProfile = state.userProfiles.find(p => p.id === memberId);
      // Get role from household.permissions, defaulting if not found
      const memberRole = household.permissions?.[memberId]?.role || Role.MEMBER;

      // Get joinedAt from permissions or use a placeholder/current date
      const memberJoinedAt = household.permissions?.[memberId]?.joinedAt || new Date().toISOString();

      return {
        id: memberId, // Ensure 'id' is always present for HouseholdMember
        userId: memberId,
        name: userProfile?.name || 'Unknown User',
        email: userProfile?.email || 'N/A',
        role: memberRole,
        joinedAt: memberJoinedAt,
        avatar: userProfile?.avatar,
      } as HouseholdMember;
    });
  }, [household, state.userProfiles]);


  // 🛡️ REVISED: Derive current user role directly from household.permissions
  const currentUserRole: Role = useMemo(() => {
    if (!household || !currentUserId) {
      return Role.MEMBER; // Default to MEMBER if no household or user ID
    }
    return household.permissions?.[currentUserId]?.role || Role.MEMBER;
  }, [household?.permissions, currentUserId, household]);

  const isOwner = currentUserRole === Role.OWNER;
  const isAuthorized = isOwner || currentUserRole === Role.ADMIN;

  const activeInvite = useMemo(() => {
    if (!household?.activeInvite) return null;
    const invite = household.activeInvite;
    const expiry = new Date(invite.expiresAt).getTime();
    return Date.now() > expiry ? null : invite;
  }, [household]);

  useEffect(() => {
    const fetchLimit = async () => {
      try {
        const limitsDoc = await getDoc(doc(db, 'settings', 'limits'));
        if (limitsDoc.exists()) {
          const data = limitsDoc.data();
          setMaxHouseholdsPerUser(data.maxHouseholdsPerUser || 2);
        }
      } catch (error) {
        console.error("Error fetching household limit:", error);
      }
    };
    fetchLimit();
  }, []);

  // --- ACTIONS ---
  const handleSwitchHousehold = (id: string) => {
    dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: id });
    setActiveTab('OVERVIEW');
  };

  const handleCreateInvite = async () => {
    if (!household || !isAuthorized || !currentUserId) return;
    setIsCreatingInvite(true);
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      await setDoc(doc(db, "invites", code), {
        code, householdId: household.id, householdName: household.name,
        expiresAt, createdBy: currentUserId, createdAt: new Date().toISOString()
      });
      await updateDoc(doc(db, "households", household.id), {
        activeInvite: { code, expiresAt, createdBy: currentUserId, createdAt: new Date().toISOString() }
      });
    } catch (e) { console.error(e); } finally { setIsCreatingInvite(false); }
  };

  // New handler for creating a household
  const handleInitializeNewHousehold = async () => {
    console.log("handleInitializeNewHousehold called."); // 🌟 NEW: Confirm function is called
    setNewHouseholdNameError(null);

    if (!currentUser || !currentUserId) {
      console.log("handleInitializeNewHousehold: User not logged in."); // 🌟 NEW: Log guard condition
      alert('You must be logged in to create a household.');
      return;
    }

    if (!newHouseholdName.trim()) {
      console.log("handleInitializeNewHousehold: Household name empty."); // 🌟 NEW: Log guard condition
      setNewHouseholdNameError('Household name cannot be empty.');
      return;
    }

    const userHouseholdsCount = state.households.filter(h => h.memberIds.includes(currentUserId)).length;
    if (userHouseholdsCount >= maxHouseholdsPerUser) {
      console.log("handleInitializeNewHousehold: Household limit reached."); // 🌟 NEW: Log guard condition
      setNewHouseholdNameError(`You have reached the maximum limit of ${maxHouseholdsPerUser} households.`);
      return;
    }

    setIsCreatingHousehold(true);
    try {
      console.log("handleInitializeNewHousehold: Calling initializeHousehold service."); // 🌟 NEW: Log before service call
      const newHousehold = await initializeHousehold(currentUser, newHouseholdName.trim());
      console.log("handleInitializeNewHousehold: initializeHousehold service returned:", newHousehold); // 🌟 NEW: Log service return

      dispatch({ type: 'ADD_HOUSEHOLD', payload: newHousehold });
      dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: newHousehold.id });
      setActiveTab('OVERVIEW');
      setNewHouseholdName('');
      alert(`Household "${newHousehold.name}" created successfully!`);
    } catch (e: any) {
      console.error('handleInitializeNewHousehold: Error creating household:', e); // Detailed error logging
      setNewHouseholdNameError(`Failed to create household: ${e.message || 'Please try again.'}`);
    } finally {
      setIsCreatingHousehold(false);
    }
  };

  const handleJoinByCode = async () => {
    setJoinCodeError(null);

    if (!joinCode.trim()) {
      setJoinCodeError('Invite code cannot be empty.');
      return;
    }

    if (!currentUser || !currentUserId) {
      alert('You must be logged in to join a household.');
      return;
    }

    const userHouseholdsCount = state.households.filter(h => h.memberIds.includes(currentUserId)).length;
    if (userHouseholdsCount >= maxHouseholdsPerUser) {
      setJoinCodeError(`You have reached the maximum limit of ${maxHouseholdsPerUser} households.`);
      setJoinCode('');
      return;
    }

    setIsJoining(true);
    try {
      const joinedHousehold = await joinHouseholdWithCode(currentUser, joinCode.trim());

      dispatch({ type: 'ADD_HOUSEHOLD', payload: joinedHousehold });
      dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: joinedHousehold.id });
      setActiveTab('OVERVIEW');
      setJoinCode('');
      alert(`Welcome to "${joinedHousehold.name}"!`);
    } catch (e: any) {
      console.error('Error joining household:', e);
      setJoinCodeError(`Failed to join household: ${e.message || 'Please check the invite code and try again.'}`);
    } finally {
      setIsJoining(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    alert("Removed member");
  };

  // 🌟 NEW LOG: Log the evaluation of the disabled prop's parts
  const isHouseholdNameEmpty = !newHouseholdName.trim();
  const isCurrentUserNull = !currentUser;
  const isCreatingHouse = isCreatingHousehold;
  const isDisabledForCreation = isHouseholdNameEmpty || isCurrentUserNull || isCreatingHouse;
  
  console.log(`--- Create Household Button State ---`);
  console.log(`  - newHouseholdName.trim() is empty: ${isHouseholdNameEmpty}`);
  console.log(`  - currentUser is null:             ${isCurrentUserNull}`);
  console.log(`  - isCreatingHousehold is true:     ${isCreatingHouse}`);
  console.log(`  - Button will be disabled:         ${isDisabledForCreation}`);
  console.log(`-----------------------------------`);

 return (
    <div className="max-w-6xl mx-auto p-6 space-y-8 pb-20 animate-in fade-in duration-500">

      {/* 🧭 Dynamic Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight leading-none italic uppercase">
            {activeTab === 'OVERVIEW' && (household?.name || t('household.overview_title_fallback', 'Household Overview'))}
            {activeTab === 'MEMBERS' && t('household.tab_members', 'Member Registry')}
            {activeTab === 'SWITCHER' && t('household.tab_switcher', 'Node Network')}
          </h1>
          <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] mt-3">
            {activeTab === 'OVERVIEW' && (household?.id ? `H-ID: ${household.id.slice(0, 8)} • ACTIVE` : t('household.no_active_info', 'No Active Household Node'))}
            {activeTab === 'MEMBERS' && `Authorized Protocol Management`}
            {activeTab === 'SWITCHER' && `Multi-Tenant Environment Switching`}
          </p>
        </div>
      </div>

      {/* 📑 Tab Navigation */}
      <nav className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-1">
        {[
          ...(household ? [{ id: 'OVERVIEW', icon: Home, label: 'Overview' }] : []),
          ...(household ? [{ id: 'MEMBERS', icon: Users, label: 'Members' }] : []),
          { id: 'SWITCHER', icon: LayoutGrid, label: 'Switch / Create' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${
              activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </nav>

      {/* --- TAB 1: OVERVIEW (Only render if household is active) --- */}
      {activeTab === 'OVERVIEW' && household && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-4">
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-10 border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-5">
                <Home className="w-32 h-32" />
              </div>
              <div className="relative z-10 space-y-8">
                <div>
                  <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-4">Household Identity</h3>
                  <div className="flex items-center gap-6">
                    <div className="w-20 h-20 bg-indigo-600 rounded-[2rem] flex items-center justify-center text-white shadow-xl shadow-indigo-500/20 font-black text-3xl italic">
                      {household.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="text-3xl font-black text-slate-900 dark:text-white uppercase italic">{household.name}</h4>
                      <p className="text-xs font-bold text-slate-500 mt-1">{household.type || 'Primary Residence'}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-800">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Population</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-white italic">
                      {household.memberIds.length} <span className="text-sm text-slate-400 font-bold tracking-normal italic uppercase">/ {household.memberLimit || 6} Slots</span>
                    </p>
                  </div>
                  <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-800">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Auth Status</p>
                    <p className="text-2xl font-black text-indigo-600 italic uppercase">{currentUserRole}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            <div className="bg-indigo-600 rounded-[3rem] p-8 text-white shadow-xl shadow-indigo-500/20">
              <h3 className="text-xs font-black uppercase tracking-widest mb-2 flex items-center gap-2 italic">
                <UserPlus className="w-4 h-4" /> {t('household.invite_title', 'Expansion')}
              </h3>
              <p className="text-[10px] font-bold opacity-80 mb-8 uppercase leading-tight">Generate a temporary access token for new household members.</p>

              {activeInvite ? (
                <div className="bg-white/10 p-6 rounded-3xl border border-white/20 text-center space-y-4">
                  <h4 className="text-4xl font-black tracking-[0.2em] font-mono">{activeInvite.code}</h4>
                  <p className="text-[9px] font-black opacity-60 flex items-center justify-center gap-2 uppercase tracking-widest italic">
                    <Clock className="w-3 h-3" /> Expires: {new Date(activeInvite.expiresAt).toLocaleTimeString()}
                  </p>
                  <Button variant="outline" className="w-full h-12 rounded-2xl border-white/20 text-white font-black hover:bg-white/10" onClick={() => { navigator.clipboard.writeText(activeInvite.code); alert('Copied'); }}>
                    <Copy className="w-4 h-4 mr-2" /> Copy Key
                  </Button>
                </div>
              ) : (
                <Button className="w-full h-14 rounded-2xl bg-white text-indigo-600 hover:bg-indigo-50 font-black uppercase tracking-widest text-xs" onClick={handleCreateInvite} disabled={!isAuthorized || isCreatingInvite}>
                  {isCreatingInvite ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Generate Token'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

       {/* --- TAB 2: MEMBERS (Only render if household is active) --- */}
      {activeTab === 'MEMBERS' && household && (
        <div className="max-w-4xl animate-in fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
            <div className="p-8 border-b border-slate-100 dark:border-slate-800">
              <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest italic">Access Control Registry</h3>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {(householdMembersForDisplay || []).map((member) => {
                const isMe = member.userId === currentUserId;
                return (
                  <div key={member.userId} className="p-6 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center font-black text-slate-500 italic uppercase">
                        {member.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-black text-slate-900 dark:text-white uppercase italic flex items-center gap-2">
                          {member.name} {isMe && <span className="text-[8px] bg-indigo-600 text-white px-2 py-0.5 rounded uppercase font-black tracking-widest not-italic">YOU</span>}
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 mt-0.5">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">{member.role}</span>
                        <span className="text-[9px] font-bold text-slate-400 block mt-0.5 tracking-tighter uppercase italic">Joined {new Date(member.joinedAt || '').toLocaleDateString()}</span>
                      </div>
                      {isAuthorized && !isMe && member.role !== Role.OWNER && (
                        <button onClick={() => alert('Manage Roles')} className="p-2 text-slate-300 hover:text-rose-500 transition-colors">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* --- TAB 3: SWITCHER / CREATE (Always render) --- */}
      {activeTab === 'SWITCHER' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in">
          {/* Display existing households the user is a member of */}
          {(state.households || []).map((h: Household) => {
            const isActive = h.id === state.currentHouseholdId;
            return (
              <div
                key={h.id}
                className={`p-8 rounded-[2.5rem] border-2 transition-all group relative overflow-hidden ${
                  isActive ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl shadow-indigo-500/20' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-indigo-400'
                }`}
              >
                <div className="relative z-10 space-y-6">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl italic ${isActive ? 'bg-white/20' : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600'}`}>
                    {h.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className={`text-xl font-black uppercase italic ${isActive ? 'text-white' : 'text-slate-900 dark:text-white'}`}>{h.name}</h4>
                    <p className={`text-[10px] font-bold uppercase tracking-widest mt-1 ${isActive ? 'text-white/60' : 'text-slate-400'}`}>{h.memberIds.length} Members</p>
                  </div>
                  {!isActive && (
                    <Button
                      onClick={() => handleSwitchHousehold(h.id)}
                      className="w-full rounded-2xl h-12 font-black uppercase tracking-widest text-[10px] bg-slate-900 text-white hover:bg-slate-800"
                    >
                      Activate Node <ArrowRight className="w-3 h-3 ml-2" />
                    </Button>
                  )}
                  {isActive && (
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-white/80 italic">
                      <Zap className="w-3 h-3 fill-white" /> Currently Active
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Create New Card (Always visible in SWITCHER tab) */}
          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border-2 border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
            <h3 className="text-xs font-black uppercase text-indigo-600 tracking-widest mb-2 flex items-center gap-2 italic">
              <Building2 className="w-4 h-4" /> New Node Initialization
            </h3>
            <p className="text-[10px] text-slate-400 font-bold mb-6 uppercase tracking-tight">Establish a new household node for your network.</p>
            <input
              type="text"
              placeholder={t('household.new_name_placeholder', 'Name')}
              value={newHouseholdName}
              onChange={(e) => {
                setNewHouseholdName(e.target.value);
                setNewHouseholdNameError(null); // Clear error on change
              }}
              className="w-full p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-center text-xl uppercase tracking-[0.3em] text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            {newHouseholdNameError && (
              <p className="flex items-center gap-2 text-rose-500 text-xs font-bold mt-2">
                <AlertCircle className="w-4 h-4" /> {newHouseholdNameError}
              </p>
            )}
            <Button
              onClick={handleInitializeNewHousehold}
              className="w-full h-14 rounded-2xl font-black uppercase tracking-widest bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
              isLoading={isCreatingHousehold}
              disabled={!newHouseholdName.trim() || !currentUser || isCreatingHousehold}
            >
              {isCreatingHousehold ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} {t('household.create_new_button', 'Initialize New Node')}
            </Button>
          </div>

          {/* Join with Code input area (Always visible in SWITCHER tab) */}
          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 border-2 border-indigo-500/20 shadow-xl space-y-4">
            <h3 className="text-xs font-black uppercase text-indigo-600 tracking-widest mb-2 flex items-center gap-2 italic">
              <DoorOpen className="w-4 h-4" /> Migration Protocol
            </h3>
            <p className="text-[10px] text-slate-400 font-bold mb-6 uppercase tracking-tight">Enter a key shared by another household to merge nodes.</p>
            <input
              type="text"
              placeholder={t('household.code_placeholder', 'INVITE CODE')}
              value={joinCode}
              onChange={(e) => {
                setJoinCode(e.target.value);
                setJoinCodeError(null);
              }}
              className="w-full p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-center text-xl uppercase tracking-[0.3em] text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none"
            />
            {joinCodeError && (
              <p className="flex items-center gap-2 text-rose-500 text-xs font-bold mt-2">
                <AlertCircle className="w-4 h-4" /> {joinCodeError}
              </p>
            )}
            <Button
              onClick={handleJoinByCode}
              className="w-full h-14 rounded-2xl font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              disabled={joinCode.length < 5 || isJoining || !currentUser}
              isLoading={isJoining} // 🌟 Ensure isLoading is passed
            >
              {isJoining ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />} {t('household.join_button', 'Join Household')}
            </Button>
          </div>
        </div>
      )}

    </div>
  );
};